import { PracticeRestriction } from 'src/app/shared/models/practiceRestriction';
import { isPatientPracticeRestriction } from './practice-restriction.utils';

export interface DateRangeBounds {
    from: Date;
    /** null = open-ended */
    to: Date | null;
}

export interface SelfCareAgreementLike {
    id?: number;
    active?: boolean;
    providerId?: number | null;
    fromDate?: Date | string | null;
    toDate?: Date | string | null;
    practice?: { id?: number; restriction?: PracticeRestriction };
}

/**
 * Autocuidado no tiene prestador. Si el backend/legacy guardó patientId como providerId, se normaliza a null.
 */
export function normalizeSelfCareAgreementProviderId(
    agreement: SelfCareAgreementLike | null | undefined,
    patientId?: number | null,
): number | null {
    if (!agreement) {
        return null;
    }
    if (!isPatientPracticeRestriction(agreement.practice?.restriction)) {
        return agreement.providerId ?? null;
    }
    // Self-care: never expose a provider. Legacy rows may have providerId === patientId.
    if (agreement.providerId == null) {
        return null;
    }
    if (patientId != null && Number(agreement.providerId) === Number(patientId)) {
        return null;
    }
    // Even unexpected non-patient providerId is cleared for self-care UI/model.
    return null;
}

export function applySelfCareAgreementProviderNormalization<T extends SelfCareAgreementLike>(
    agreement: T,
    patientId?: number | null,
): T {
    if (!agreement) {
        return agreement;
    }
    return {
        ...agreement,
        providerId: normalizeSelfCareAgreementProviderId(agreement, patientId),
    };
}

/** Payload for creating a self-care agreement (no provider). */
export function buildSelfCareAgreementCreatePayload(fromDate: Date | string, toDate?: Date | string | null): {
    providerId: null;
    providerFee: number;
    fromDate: Date | string;
    toDate: Date | string | null | undefined;
} {
    return {
        providerId: null,
        providerFee: 0,
        fromDate,
        toDate: toDate ?? null,
    };
}

function parseLocalDate(value: Date | string): Date {
    if (value instanceof Date) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    const isoDay = typeof value === 'string' ? value.slice(0, 10) : '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(isoDay)) {
        const [y, m, d] = isoDay.split('-').map(Number);
        return new Date(y, m - 1, d);
    }
    const parsed = new Date(value);
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function toStartOfDay(value: Date | string): Date {
    const d = parseLocalDate(value);
    d.setHours(0, 0, 0, 0);
    return d;
}

function toEndOfDay(value: Date | string): Date {
    const d = parseLocalDate(value);
    d.setHours(23, 59, 59, 999);
    return d;
}

export function intersectClosedOpenRanges(
    aFrom: Date | string | null | undefined,
    aTo: Date | string | null | undefined,
    bFrom: Date | string | null | undefined,
    bTo: Date | string | null | undefined,
): DateRangeBounds | null {
    if (!aFrom || !bFrom) {
        return null;
    }
    const startA = toStartOfDay(aFrom);
    const startB = toStartOfDay(bFrom);
    const endA = aTo ? toEndOfDay(aTo) : null;
    const endB = bTo ? toEndOfDay(bTo) : null;

    const from = startA > startB ? startA : startB;
    let to: Date | null;
    if (endA && endB) {
        to = endA < endB ? endA : endB;
    } else if (endA) {
        to = endA;
    } else if (endB) {
        to = endB;
    } else {
        to = null;
    }

    if (to && from.getTime() > to.getTime()) {
        return null;
    }
    return { from, to };
}

/**
 * Picks the best active agreement for scheduling self-care and computes min/max as intersection
 * with the provision fee validity.
 */
export function resolveSelfCareSchedulingContext(
    agreements: SelfCareAgreementLike[] | null | undefined,
    provisionFee: { fromDate?: Date | string | null; toDate?: Date | string | null } | null | undefined,
    patientId?: number | null,
): { agreement: SelfCareAgreementLike; minDate: Date; maxDate: Date | null } | null {
    if (!provisionFee?.fromDate || !agreements?.length) {
        return null;
    }

    const candidates = agreements
        .filter((ag) => ag?.active !== false)
        .map((ag) => applySelfCareAgreementProviderNormalization(ag, patientId))
        .map((ag) => {
            const intersection = intersectClosedOpenRanges(
                provisionFee.fromDate,
                provisionFee.toDate,
                ag.fromDate,
                ag.toDate,
            );
            return { ag, intersection };
        })
        .filter((row) => !!row.intersection);

    if (!candidates.length) {
        return null;
    }

    const first = candidates[0];
    return {
        agreement: first.ag,
        minDate: first.intersection!.from,
        maxDate: first.intersection!.to,
    };
}

/** True when attention date falls inside [min,max] (max null = open end). */
export function isDateWithinSchedulingBounds(
    date: Date | string,
    minDate: Date | null | undefined,
    maxDate: Date | null | undefined,
): boolean {
    if (!minDate) {
        return true;
    }
    const d = toStartOfDay(date);
    if (d.getTime() < toStartOfDay(minDate).getTime()) {
        return false;
    }
    if (maxDate && d.getTime() > toEndOfDay(maxDate).getTime()) {
        return false;
    }
    return true;
}

/** Keys shown in schedule-case "Se repite" chips (orden L→D). */
export const REPEAT_DAY_CHIP_KEYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'] as const;

export function createDefaultRepeatDayChipsSelected(): Map<string, boolean> {
    const map = new Map<string, boolean>();
    REPEAT_DAY_CHIP_KEYS.forEach((key) => map.set(key, false));
    return map;
}
